import React, { useEffect, useMemo, useState } from 'react';
import { Button, Empty, Form, Select, Space, message, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { optionService } from '../services/option';
import SimpleWysiwygEditor from './SimpleWysiwygEditor';
import {
  get_list_help_page_label,
  LIST_HELP_PAGE_OPTIONS,
} from '../config/list-help-page-options';
import {
  DEFAULT_LIST_HELP_CONFIGS,
  ListHelpConfig,
  merge_list_help_configs,
} from '../config/list-help-defaults';

const { Text } = Typography;

const LIST_HELP_OPTION_KEY = 'list_page_help_configs';

/**
 * 规范化列表帮助 HTML，避免富文本在反复保存后累积空段落或空列表项。
 */
const normalize_help_content = (content: string): string => {
  return String(content || '')
    .replace(/<p><br><\/p>/gi, '')
    .replace(/<p>\s*<\/p>/gi, '')
    .replace(/<li>\s*(?:<br\s*\/?>|&nbsp;|\s)*<\/li>/gi, '')
    .replace(/<(ol|ul)>\s*<\/\1>/gi, '')
    .trim();
};

const ListHelpSettings: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selected_index, set_selected_index] = useState(0);
  const help_items = Form.useWatch('help_items', form) as ListHelpConfig[] | undefined;

  const normalized_help_items = useMemo(() => {
    return Array.isArray(help_items) ? help_items : [];
  }, [help_items]);

  const selected_page_keys = useMemo(() => {
    return new Set(
      normalized_help_items
        .map((item) => String(item?.page_key || '').trim())
        .filter(Boolean)
    );
  }, [normalized_help_items]);

  useEffect(() => {
    const load_configs = async () => {
      try {
        setLoading(true);
        const saved_configs = await optionService.get(LIST_HELP_OPTION_KEY, []);
        const merged_configs = merge_list_help_configs(Array.isArray(saved_configs) ? saved_configs : []);
        form.setFieldsValue({
          help_items: merged_configs,
        });
        set_selected_index(0);
      } catch (error) {
        console.error('加载常见问题配置失败:', error);
        form.setFieldsValue({
          help_items: DEFAULT_LIST_HELP_CONFIGS,
        });
        set_selected_index(0);
      } finally {
        setLoading(false);
      }
    };

    load_configs();
  }, [form]);

  useEffect(() => {
    if (normalized_help_items.length === 0) {
      if (selected_index !== 0) {
        set_selected_index(0);
      }
      return;
    }

    if (selected_index > normalized_help_items.length - 1) {
      set_selected_index(normalized_help_items.length - 1);
    }
  }, [normalized_help_items.length, selected_index]);

  const handle_save = async (values: { help_items?: ListHelpConfig[] }) => {
    const normalized_items = (values.help_items || [])
      .map((item) => ({
        page_key: String(item.page_key || '').trim(),
        help_content: normalize_help_content(item.help_content || ''),
      }))
      .filter((item) => item.page_key && item.help_content);

    try {
      setLoading(true);
      await optionService.set(LIST_HELP_OPTION_KEY, normalized_items);
      message.success('常见问题配置已保存');
    } catch (error) {
      console.error('保存常见问题配置失败:', error);
      message.error('保存常见问题配置失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        onFinish={handle_save}
      >
        <Form.List name="help_items">
          {(fields, { add, remove }) => (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '280px minmax(0, 1fr)',
                gap: 16,
                alignItems: 'start',
              }}
            >
              <div
                style={{
                  border: '1px solid #f0f0f0',
                  borderRadius: 8,
                  overflow: 'hidden',
                  background: '#fff',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 16px',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <Text strong>功能模块</Text>
                  <Button
                    type="dashed"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      add({
                        page_key: '',
                        help_content: '',
                      });
                      set_selected_index(fields.length);
                    }}
                  >
                    添加
                  </Button>
                </div>

                {fields.length === 0 ? (
                  <div style={{ padding: 24 }}>
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无常见问题" />
                  </div>
                ) : (
                  <div style={{ maxHeight: 640, overflowY: 'auto' }}>
                    {fields.map((field, index) => {
                      const current_item = normalized_help_items[index];
                      const current_page_key = String(current_item?.page_key || '').trim();
                      const display_title = current_page_key
                        ? get_list_help_page_label(current_page_key)
                        : `常见问题 ${index + 1}`;
                      const is_active = index === selected_index;

                      return (
                        <div
                          key={field.key}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '10px 12px',
                            borderBottom: index === fields.length - 1 ? 'none' : '1px solid #f5f5f5',
                            background: is_active ? '#e6f4ff' : '#fff',
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => set_selected_index(index)}
                            style={{
                              flex: 1,
                              textAlign: 'left',
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              padding: 0,
                              fontSize: 14,
                              color: is_active ? '#1677ff' : '#262626',
                              fontWeight: is_active ? 600 : 400,
                            }}
                          >
                            {display_title}
                          </button>
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => {
                              remove(field.name);

                              if (fields.length === 1) {
                                set_selected_index(0);
                                return;
                              }

                              if (index < selected_index) {
                                set_selected_index(selected_index - 1);
                                return;
                              }

                              if (index === selected_index) {
                                set_selected_index(Math.max(0, selected_index - 1));
                              }
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div
                style={{
                  border: '1px solid #f0f0f0',
                  borderRadius: 8,
                  background: '#fff',
                  padding: 16,
                  minHeight: 420,
                }}
              >
                {fields.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="请先添加一个常见问题"
                    style={{ marginTop: 80 }}
                  />
                ) : (
                  (() => {
                    const current_field = fields[selected_index];

                    if (!current_field) {
                      return (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description="当前常见问题不可用，请重新选择"
                          style={{ marginTop: 80 }}
                        />
                      );
                    }

                    const current_page_key = String(
                      normalized_help_items[selected_index]?.page_key || ''
                    ).trim();

                    return (
                      <div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 16,
                          }}
                        >
                          <div>
                            <Text strong>当前编辑</Text>
                          </div>
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => {
                              remove(current_field.name);

                              if (fields.length === 1) {
                                set_selected_index(0);
                                return;
                              }

                              set_selected_index(Math.max(0, selected_index - 1));
                            }}
                          >
                            删除当前项
                          </Button>
                        </div>

                        <Form.Item
                          name={[current_field.name, 'page_key']}
                          label="功能模块"
                          rules={[{ required: true, message: '请输入功能模块' }]}
                        >
                          <Select
                            showSearch
                            placeholder="请选择页面"
                            optionFilterProp="label"
                            options={LIST_HELP_PAGE_OPTIONS.map((option) => {
                              const is_used_by_other = selected_page_keys.has(option.value) && option.value !== current_page_key;

                              return {
                                value: option.value,
                                label: `${option.label} (${option.value})`,
                                disabled: is_used_by_other,
                              };
                            })}
                          />
                        </Form.Item>

                        <Form.Item
                          name={[current_field.name, 'help_content']}
                          label="帮助内容"
                          rules={[{ required: true, message: '请输入帮助内容' }]}
                        >
                          <SimpleWysiwygEditor
                            key={current_page_key || String(current_field.name)}
                            placeholder="请输入帮助内容。"
                          />
                        </Form.Item>
                      </div>
                    );
                  })()
                )}
              </div>
            </div>
          )}
        </Form.List>

        <Space style={{ marginTop: 16 }}>
          <Button type="primary" htmlType="submit" loading={loading}>
            保存配置
          </Button>
        </Space>
      </Form>
    </div>
  );
};

export default ListHelpSettings;
